import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import BrandLockup from '../components/BrandLockup';
import { ErrorIcon } from '../components/icons';
import { api } from '../App';
import Button from '@shared/ui/Button';
import AccountCredentialFields from '../components/auth/AccountCredentialFields';
import { credentialFields, passwordsMatch } from '../components/auth/credentialSchema';

const acceptInvitationSchema = z
  .object(credentialFields)
  .refine(passwordsMatch.check, passwordsMatch.options);

type AcceptInvitationFormData = z.infer<typeof acceptInvitationSchema>;

const AcceptInvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<AcceptInvitationFormData>({
    resolver: zodResolver(acceptInvitationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      username: '',
      password: '',
      confirmPassword: '',
    },
  });

  const {
    data: invitationResponse,
    isLoading: isLoadingInvitation,
    isError: isInvitationError,
  } = useQuery(
    ['validate-invitation', token],
    async () => {
      const { data } = await api.get(`/auth/validate-invitation/${token}`);
      return data;
    },
    {
      enabled: !!token,
      retry: false,
    },
  );

  const registerMutation = useMutation(
    async (payload: AcceptInvitationFormData) => {
      const { data } = await api.post('/auth/register-invited', {
        token,
        ...payload,
      });
      return data;
    },
    {
      onError: (err: any) => {
        // The endpoint 400s with a specific, actionable reason — the invitation
        // was already used, the email already has an account, the username is
        // taken. Without an onError the mutation swallowed all of it and the
        // button looked inert.
        const res = err?.response?.data;
        const message =
          res?.error ||
          res?.errors?.[0]?.msg ||
          'Could not complete your registration. Please try again.';
        setSubmitError(message);
        toast.error(message);
      },
      onSuccess: (data) => {
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('user', JSON.stringify(data.data.user));
        // Redirect to dashboard or directly to workspace if available
        const workspaceId = data.data.workspaceId;
        if (workspaceId) {
          navigate(`/workspaces/${workspaceId}`);
        } else {
          navigate('/dashboard');
        }
      },
    },
  );

  const onSubmit = (values: AcceptInvitationFormData) => {
    setSubmitError(null);
    registerMutation.mutate(values);
  };

  const isInvalid = isInvitationError || invitationResponse?.success === false;

  /*
    An invited address that already has an account cannot create a second one.
    register-invited does reject it — but only on submit, after a username and
    password have been chosen that will never be used. Route those visitors to
    sign-in instead, and if they are already signed in, let them accept outright.
  */
  const invitation = invitationResponse?.data;
  const accountExists = Boolean(invitation?.accountExists);
  const isSignedIn = Boolean(localStorage.getItem('token'));

  const acceptMutation = useMutation(
    async () => {
      const { data } = await api.post(`/workspaces/invitations/${invitation?.invitationId}/accept`);
      return data;
    },
    {
      onError: (err: any) => {
        // Covers the cases worth distinguishing: signed in as a different user
        // (the invitation is matched against the session's own email), already a
        // member, or expired. The server's wording is the useful one.
        const res = err?.response?.data;
        const message =
          res?.message ||
          res?.error ||
          'Could not accept this invitation. Please make sure you are signed in as the invited address.';
        setSubmitError(message);
        toast.error(message);
      },
      onSuccess: () => {
        toast.success('Invitation accepted');
        navigate(invitation?.workspaceId ? `/workspaces/${invitation.workspaceId}` : '/dashboard');
      },
    },
  );

  // Return here after signing in so the accept is one click, not a hunt through
  // a pending-invitations list.
  const signInHref = `/login?returnUrl=${encodeURIComponent(`/accept-invitation/${token}`)}`;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border-light dark:border-border-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <BrandLockup />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl w-full space-y-8">
          {isLoadingInvitation ? (
            <div className="text-center text-text-secondary dark:text-text-dark-secondary">Validating invitation…</div>
          ) : isInvalid ? (
            <div className="card">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-2">Invitation unavailable</h2>
                <p className="text-text-secondary dark:text-text-dark-secondary mb-4">
                  This invitation link is invalid or has expired. Please contact the person who invited you to request a
                  new invitation.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center text-text-secondary dark:text-text-dark-secondary">
                <h2 className="text-3xl font-bold text-text-primary dark:text-text-dark-primary">Accept Your Invitation</h2>
                {invitationResponse?.data?.workspaceName ? (
                  <>
                    <p className="mt-2">
                      Join the <strong>{invitationResponse.data.workspaceName}</strong> workspace
                      {invitationResponse.data.organizationName && (
                        <>
                          {' '}
                          at <strong>{invitationResponse.data.organizationName}</strong>
                        </>
                      )}
                      .
                    </p>
                    <p className="mt-1">
                      {accountExists
                        ? 'This address already has an account — sign in and you’ll be added to this workspace.'
                        : 'You’ll create your login and be added directly to this workspace.'}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="mt-2">
                      Join <strong>{invitationResponse?.data?.organizationName}</strong> as an organization admin.
                    </p>
                    <p className="mt-1">
                      {accountExists
                        ? 'This address already has an account — sign in and you’ll be added to this organization.'
                        : 'You’ll create your login and be added directly to this organization.'}
                    </p>
                  </>
                )}
              </div>

              <div className="card">
                <div className="p-6 space-y-6">
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-text-tertiary dark:text-text-dark-tertiary">Invited email</span>
                      <span className="font-medium text-text-primary dark:text-text-dark-primary">
                        {invitationResponse?.data?.email}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-text-tertiary dark:text-text-dark-tertiary">Role</span>
                      <span className="font-medium text-text-primary dark:text-text-dark-primary">
                        {invitationResponse?.data?.role?.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {accountExists ? (
                    <div className="space-y-4">
                      {submitError && (
                        <div
                          role="alert"
                          className="flex items-start gap-2 rounded-lg border border-error-200 dark:border-error-800 bg-error-50 dark:bg-error-900/20 px-3 py-2"
                        >
                          <ErrorIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-error-600 dark:text-error-400" />
                          <p className="text-sm text-error-700 dark:text-error-300">{submitError}</p>
                        </div>
                      )}

                      {isSignedIn ? (
                        <Button
                          type="button"
                          variant="primary"
                          className="w-full flex items-center justify-center"
                          disabled={acceptMutation.isLoading}
                          onClick={() => {
                            setSubmitError(null);
                            acceptMutation.mutate();
                          }}
                        >
                          {acceptMutation.isLoading ? (
                            'Accepting…'
                          ) : (
                            <>
                              Accept Invitation
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="primary"
                          className="w-full flex items-center justify-center"
                          onClick={() => navigate(signInHref)}
                        >
                          Sign In &amp; Accept
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      )}

                      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary text-center">
                        {isSignedIn
                          ? 'Make sure you are signed in as the invited address above.'
                          : 'Signing in returns you here to finish accepting.'}
                      </p>
                    </div>
                  ) : (
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <AccountCredentialFields
                      register={form.register}
                      errors={form.formState.errors}
                      withIcons
                    />

                    {submitError && (
                      <div
                        role="alert"
                        className="flex items-start gap-2 rounded-lg border border-error-200 dark:border-error-800 bg-error-50 dark:bg-error-900/20 px-3 py-2"
                      >
                        <ErrorIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-error-600 dark:text-error-400" />
                        <p className="text-sm text-error-700 dark:text-error-300">{submitError}</p>
                      </div>
                    )}

                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full flex items-center justify-center"
                      disabled={registerMutation.isLoading}
                    >
                      {registerMutation.isLoading ? (
                        'Creating your account…'
                      ) : (
                        <>
                          Join Workspace
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>

                    <p className="mt-3 text-xs text-text-tertiary dark:text-text-dark-tertiary text-center">
                      By joining, you agree to our{' '}
                      <a
                        href="https://capitolcommons.ai/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Terms of Use
                      </a>{' '}
                      and{' '}
                      <a
                        href="https://capitolcommons.ai/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Privacy Policy
                      </a>
                      .
                    </p>
                  </form>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitationPage;
